import { toRaw } from 'vue'
import { defineStore, acceptHMRUpdate } from 'pinia'
import { apiClient } from "@/helpers/api.js"
import { handleApiError } from '@/services/errorHandler'
import { getDefaultTemplateForStore } from '@/helpers/connectionsAndExport.js'
import {
  RECOMMENDED_CONNECTION_CHANNELS,
  ALL_CONNECTION_CHANNELS,
  DEFAULT_TEMPLATE,
  EBAY_CHANNEL,
  SHOPIFY_CHANNEL,
  SIMPLE_AUCTION_CHANNEL,
  COLLX_CHANNEL,
  WHATNOT_CHANNEL,
  DEFAULT_TEMPLATE_PER_CONNECTION_TYPE,
  CONNECTION_LISTING_TEMPLATES_NAMES_PER_CONNECTION,
  INVENTORY_BETA_SURVEY_TRIGGER,
} from '@/constants'
import { useUserStore } from '@/stores/UserStore'
import { useBatchStore } from '@/stores/BatchStore'
import { buildPagination } from '@/helpers/pagination'

// One `/export-profiles?type=` answer per marketplace, shared by every caller
// for a few seconds. A page load asked for the same lists from several places
// at once — the Inventory page, the batch grid hook, the route guards — so
// opening a batch from Inventory sent nine requests for five lists. A request
// in flight is reused as-is; a finished one is reused briefly, and any write
// to a profile drops them all.
const EXPORT_PROFILES_REUSE_MS = 10000
const exportProfileRequests = new Map()

const loadExportProfiles = (listingType) => {
  const cached = exportProfileRequests.get(listingType)
  if (cached && Date.now() - cached.at < EXPORT_PROFILES_REUSE_MS) return cached.request

  const request = apiClient().get(`/export-profiles?type=${listingType}`).then(({ data }) => data)
  exportProfileRequests.set(listingType, { request, at: Date.now() })

  // A failure is not worth remembering: the next caller asks again.
  request.catch(() => exportProfileRequests.delete(listingType))

  return request
}

const forgetExportProfiles = () => exportProfileRequests.clear()

export const useConnectionStore = defineStore('ConnectionStore', {
  state: () => {
    return {
      channels: [...ALL_CONNECTION_CHANNELS],
      recommendedChannels: [ ...RECOMMENDED_CONNECTION_CHANNELS ],

      listingTemplates: [],
      marketplaceListingTemplates: [],
      whatnotListingTemplates: [],
      collxListingTemplates: [],
      shopifyListingTemplates: [],
      loadingListingTemplates: false,
      marketplaceLoadingListingTemplates: false,
      selectedTemplateId: null,
      selectedTemplate: { ...DEFAULT_TEMPLATE },

      selectedTemplatePerConnection: { ...DEFAULT_TEMPLATE_PER_CONNECTION_TYPE },

      openedOverviewDialog: false,
      addingTemplate: false,
      templateCreation: false,

      isItExport: false,
      isItEbayConnection: false,

      steps: [ ],

      loadingExportsOrImports: false,
      exports: [],
      export: {},

      imports: [],
      import: {},

      downloadData: null,

      shouldBeUpdated: false,

      exportedChannelInfo: {},
      exportedChannelInfoList: [],
      exportedErrorsInfo: null,
      showExportToast: false,

      collxUser: null,
      collxAuthLoader: false,
      collxAuthError: null,

      connectionAccountLoader: false,
      connectionAccountInfo: null,
      collxAuthFailed: false,

      collxSending: false,
      collxSent: false,
      collxSendError: false,

      ebaySending: false,
      ebaySent: false,
      ebaySendError: false,
      ebayAuthFailed: false,

      whatnotSending: false,
      whatnotSent: false,
      whatnotSendError: false,

      shopifyAuthLoader: false,
      shopifyAuthFailed: false,

      shopifySending: false,
      shopifySent: false,
      shopifySendError: false,

      pagination: null,

      listedErrorModal: false,

      policies: [],
      ebayStoreCategoriesNoSubscription: false,
      ebayStoreCategories: [],
      storeCategoriesLevel: 1,
      selectedStoreCategory: null,
      selectedStoreParentCategory: null,
      loadingPolicies: false,
      isEbayDirect: false,

      metafieldsAvailable: [],
      tagsAvailable: [],
      metafields: [],
      tags: [],

      /**
       * Shopify locations. `shopifyLocations` is the store's list of
       * physical/virtual locations; `shopifyDefaultLocationId` is the one CDP
       * lists into. Both come from the same GET, so one fetch answers "can the
       * user choose?" and "what did they choose?".
       */
      shopifyLocations: [],
      shopifyDefaultLocationId: null,
      loadingShopifyLocations: false,
      /**
       * Did the locations request actually SUCCEED? An empty list means "this
       * store has no locations" only if we got an answer — a failed call (not
       * connected, scope missing, network) leaves the same empty array, and
       * telling a disconnected user their store has no locations would be a
       * lie the UI can't back up.
       */
      shopifyLocationsLoaded: false
    }
  },
  getters: {
    addedChannels() {
      const userStore = useUserStore()
      const isEbay = userStore.credentialByType('ebay')?.enabled || userStore.user?.ebay_enabled
      const isShopify = userStore.credentialByType('shopify')?.enabled || userStore.user?.shopify_enabled
      const isAuction = userStore.user?.simple_auction_enabled || userStore.user?.simple_auction_enabled
      const isCollx = userStore.credentialByType('collx')?.enabled || userStore.user?.collx_enabled
      const isWhatnot = userStore.credentialByType('whatnot')?.enabled || userStore.user?.whatnot_enabled
      const channels = []

      if (isEbay) {
        channels.push({ ...EBAY_CHANNEL })
      }

      if (isShopify) {
        channels.push({ ...SHOPIFY_CHANNEL })
      }

      if (isAuction) {
        channels.push({ ...SIMPLE_AUCTION_CHANNEL })
      }

      if (isCollx) {
        channels.push({ ...COLLX_CHANNEL })
      }

      if (isWhatnot) {
        channels.push({ ...WHATNOT_CHANNEL })
      }

      return channels
    },
    remainChannels: (state) => {
      const userStore = useUserStore()
      return state.channels.filter(channel => {
        // For Whatnot, check both user enabled and flags
        if (channel.name === 'Whatnot') {
          return !state.addedChannels.some(item => channel.name === item.name)
        }
        // For other channels, use existing logic
        return !state.addedChannels.some(item => channel.name === item.name)
      })
    },
  },
  actions: {
    removeChannel(channel) {
      let idx = this.addedChannels.findIndex(el => el.name === channel.name)
      this.addedChannels.splice(idx, 1)
    },

    setReturnsFieldsToNull() {
      this.selectedTemplate.returns_within = null
      this.selectedTemplate.refund_given_as = null
      this.selectedTemplate.return_shipping_paid_by = null
    },

    async updateTemplate(id, body, type = 'listingTemplates') {
      try {
        const { data } = await apiClient().patch(`/export-profiles/${id}`, body)
        const ebayTemplateTypes = ['listingTemplates', 'marketplaceListingTemplates']

        this[type] = this[type].reduce((acc, item) => {
          if (item.id !== id) {
            acc.push(item)
          } else if (ebayTemplateTypes.includes(type) && data.profile) {
            acc.push({
              ...data,
              profile: {
                ...data.profile,
                auto_accept_offer: Boolean(data.profile?.auto_accept_offer),
                auto_decline_offer: Boolean(data.profile?.auto_decline_offer),
              },
            })
          } else {
            acc.push({
              ...item,
              ...body,
              ...data,
              listing_price: Boolean((data.listing_price ?? body.listing_price ?? item.listing_price)),
              calculated_method: Boolean((data.calculated_method ?? body.calculated_method ?? item.calculated_method)),
            })
          }

          return acc
        }, [])

        this.refreshSelectedTemplate(id, type)
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'updateTemplate' })
      } finally {
        // After the write lands, so a list fetched meanwhile isn't reused.
        forgetExportProfiles()
      }
    },

    /**
     * Re-point every connection that LISTS with this template at the freshly
     * saved object.
     *
     * `updateTemplate` rebuilds the templates LIST, but validation reads
     * `selectedTemplatePerConnection` — a snapshot taken when the template was
     * first selected. Nothing was refreshing that snapshot, and
     * `ensureDefaultTemplatesSelected` deliberately skips any slot that already
     * holds an id, so the stale copy survived navigation and even a return trip
     * to the connections screen. Only a hard reload cleared it.
     *
     * What the dealer saw: a card under the marketplace's price floor is
     * blocked, they turn on "round up prices" in the template, come back, and
     * the same error is still there — because the check was still reading the
     * pre-save copy with the toggle off. Every other consumer of the slot had
     * the same bug (eBay policy errors, scheduled start date, category), it was
     * just less visible: those settings are edited far from where they're
     * enforced.
     *
     * Assigning a NEW object is what makes it land — `PublishCardsMenu` watches
     * this slot deeply and recomputes the blocking flags from it.
     */
    refreshSelectedTemplate(id, type = 'listingTemplates') {
      const updated = this[type]?.find(item => String(item?.id) === String(id))

      if (!updated) return

      const next = { ...this.selectedTemplatePerConnection }
      const touched = Object.keys(next).filter(key => String(next[key]?.id) === String(id))

      if (!touched.length) return

      touched.forEach(key => { next[key] = updated })

      this.selectedTemplatePerConnection = next
    },

    /**
     * Point every connection at its DEFAULT template, unless the user has
     * already picked one.
     *
     * This used to live inside `PublishCardsMenu.togglePublishMenu`, which made
     * "which template are we listing with" a side effect of opening that one
     * menu — and `onUnmounted` wiped it again. Every other entry point (the
     * grid listings cell above all) then validated against an EMPTY template:
     * `hasEbayPoliciesTemplateErrors` reads
     * `selectedTemplatePerConnection['ebay-direct']` and can't tell "no
     * template loaded" from "template missing its policies", so a perfectly
     * good template showed as "add all required fields to your connection
     * template" — and "View Template" opened a blank create form, because it
     * opens whatever that same slot holds.
     *
     * Idempotent and non-destructive: an entry that already carries an `id` is
     * the user's choice and is left alone. The `[0]` fallback covers a list
     * whose templates somehow all have `default: false`; without it the slot
     * would stay empty and we'd be back to the false error.
     */
    ensureDefaultTemplatesSelected() {
      const next = { ...this.selectedTemplatePerConnection }

      Object.entries(CONNECTION_LISTING_TEMPLATES_NAMES_PER_CONNECTION).forEach(([key, listName]) => {
        if (next[key]?.id) return

        const fallback = this[listName]?.find(t => t.default) || this[listName]?.[0]

        if (fallback) next[key] = fallback
      })

      this.selectedTemplatePerConnection = next
    },

    async fetchTemplates(type = 'listingTemplates', listingType = 'ebay') {
      const userStore = useUserStore()
      try {
        this.loadingListingTemplates = true

        const data = await loadExportProfiles(listingType)
        const templates = Array.isArray(data) ? data : (data?.results ?? [])
        this[type] = templates.reduce((acc, item) => {
          const item_ = { ...item, auto_accept_offer: Boolean(item.auto_accept_offer), auto_decline_offer: Boolean(item.auto_decline_offer) }

          acc.push(item_)

          return acc
        }, [])
        if (this[type].length === 1 && !this.addingTemplate) {
          // A lone template is the default — but only written when it isn't
          // already, instead of a PATCH on every load of the list.
          if (!this[type][0].default) {
            await this.updateTemplate(this[type][0].id, { default: true })
          }

          this.loadingListingTemplates = false

          if (this.isItExport || userStore.user?.country !== 'US') {
            this.selectedTemplate = { ...getDefaultTemplateForStore(this.listingTemplates) }
          } else {
            this.selectedTemplate = { ...getDefaultTemplateForStore(this[type]) }
          }
          

          this.ensureDefaultTemplatesSelected()

          return this[type]
        }

        if ((this.isItExport && !this.addingTemplate) || userStore.user?.country !== 'US') {
          this.selectedTemplate = { ...getDefaultTemplateForStore(this.listingTemplates) }
        } else if (!this.addingTemplate) {
          this.selectedTemplate = { ...getDefaultTemplateForStore(this[type]) }
        }
        this.loadingListingTemplates = false
        this.ensureDefaultTemplatesSelected()

        return this[type]
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'fetchConnection' })
        this.loadingListingTemplates = false
      }
    },

    async fetchTemplateById() {
      try {
        const { data } = await apiClient().get(`/export-profiles/${this.selectedTemplateId}`)

      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'fetchTemplateById' })
      }
    },

    async createTemplate(body, type = 'listingTemplates', isAddingTemplate = false) {
      try {
        const { data } = await apiClient().post(`/export-profiles`, body)
        this[type] = [ ...this[type], data ]

        if (isAddingTemplate) {
          this.selectedTemplatePerConnection = { ...this.selectedTemplatePerConnection, [data.type]: data }
        }

        this.selectedTemplate = { ...data }
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'createTemplate' })
      } finally {
        // After the write lands, so a list fetched meanwhile isn't reused.
        forgetExportProfiles()
      }
    },

    async deleteTemplate(id, type = 'listingTemplates') {
      try {
        const { data } = await apiClient().delete(`/export-profiles/${id}`)
        this[type] = this[type].reduce((acc, item) => {
          if (item.id !== id) {
            acc.push(item)
          }

          return acc
        }, [])

        if (!this[type].length) {
          this.selectedTemplate = { ...DEFAULT_TEMPLATE }
        }
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'deleteTemplate' })
      } finally {
        // After the write lands, so a list fetched meanwhile isn't reused.
        forgetExportProfiles()
      }
    },

    async createExport(batchId, channels, cards, activeChannelList) {
      const batchStore = useBatchStore()

      const exportChannels = channels ? channels.reduce((acc, item) => {
        acc[item.field] = true

        return acc
      }, {}) : {
        ebay_enabled: true,
       }

      const cardIds = cards.map(card => card.id)

      if (!this.selectedTemplate) {
        this.selectedTemplate = this[activeChannelList][0]
      }

      const profile = getDefaultTemplateForStore(this[activeChannelList], this.selectedTemplate)
      if (profile?.ebay_category) {
        this.selectedTemplate.ebay_category = profile.ebay_category
      }

      try {
        const { data } = await apiClient().post(`/exports`, {
          status: 'pending',
          batch_id: Number(batchId),
          cards: cardIds,
          ebay_enabled: exportChannels['ebay_enabled'] || false,
          shopify_enabled: exportChannels['shopify_enabled'] || false,
          simple_auction_enabled: exportChannels['simple_auction_enabled'] || false,
          collx_enabled: false,
          export_type: profile.type,
          profile
        })

        this.export = data || {}
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'createExport' })
      }
    },

    async updateExport(exportId, channels) {
      try {
        const { data } = await apiClient().patch(`/exports/${exportId}`, {
          profile: this.selectedTemplate,
        })

      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'updateExport' })
      }
    },

    async fetchImports(query, page = 1) {
      try {
        this.loadingExportsOrImports = true
        const { data } = await apiClient().get(`/history/imports?page=${page}`, {
          params: query
        })

        this.pagination = buildPagination({ data })

        this.imports = data.items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        this.loadingExportsOrImports = false
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'fetchImports' })
        this.loadingExportsOrImports = false
      }
    },

    async fetchImportById(importId) {
      try {
        this.loadingExportsOrImports = true

        const { data } = await apiClient().get(`/history/imports/${importId}`)

        this.import = data
        //this.selectedTemplate = data.profile
        this.loadingExportsOrImports = false
      } catch (error) {
        this.loadingExportsOrImports = false
        handleApiError(error, { component: 'ConnectionStore', action: 'fetchExports' })
      }
    },

    async fetchExports(query, page = 1) {
      try {
        this.loadingExportsOrImports = true
        const { data } = await apiClient().get(`/exports?page=${page}`, {
          params: query
        })

        this.pagination = buildPagination({ data, totalItemsField: 'total_items' })

        this.exports = data.items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        this.loadingExportsOrImports = false
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'fetchExports' })
        this.loadingExportsOrImports = false
      }
    },

    async fetchExportById(exportId) {
      try {
        this.loadingExportsOrImports = true

        const { data } = await apiClient().get(`/exports/${exportId}`)

        this.export = data || {}
        this.selectedTemplate = data?.profile
        this.loadingExportsOrImports = false
      } catch (error) {
        this.loadingExportsOrImports = false
        handleApiError(error, { component: 'ConnectionStore', action: 'fetchExportById' })
      }
    },

    async downloadCsv(id, channel) {
      try {
        let url = `/exports/${id}/csvs/${channel.toLowerCase()}`

        // if (channel === 'zip') {
        //   url = `/exports/${id}/zip`
        // }

        const { data } = await apiClient().get(`${url}?token=${this.export.token}`)
        this.downloadData = data
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'downloadCsv' })
      }
    },

    async logInToCollX(email, password) {
      try {
        this.collxAuthLoader = true

        const collxResponse = await apiClient().post('/users/me/link-collx', {email, password})

        this.collxAuthFailed = false

        const userStore = useUserStore()
        const authData = localStorage.getItem("cdp_auth")
        const sessionData = JSON.parse(authData)

        userStore.user = { ...userStore.user, ...collxResponse.data.user }

        if (sessionData) {
          sessionData.user = { ...sessionData.user, ...collxResponse.data.user }
          localStorage.setItem("cdp_auth", JSON.stringify(sessionData))
        }

        localStorage.setItem("collx_user", JSON.stringify(collxResponse.data.user))
        this.collxUser = collxResponse.data?.user
        this.collxAuthLoader = false
      } catch (error) {
        this.collxAuthLoader = false

        const message = error.response?.data?.message
        if (message?.includes('Invalid password')) {
          this.collxAuthError = 'Email & Password does not match with our record.'
        } else if (message?.includes('no account found for')) {
          this.collxAuthError = "We couldn't find an account under this email."
        } else {
          this.collxAuthError = message || 'Something went wrong. Please try again.'
        }

        handleApiError(error, { component: 'ConnectionStore', action: 'logInToCollX' })
      }
    },

    async fetchConnectionAccount(connectionType) {
      try {
        this.connectionAccountLoader = true
        const response = await apiClient().get(`/users/me/${connectionType}`)

        if (response.data?.ok) {
          this.connectionAccountInfo = response.data?.data?.viewer
        }

        this.connectionAccountLoader = false
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'fetchConnectionAccount' })
        this.connectionAccountLoader = false
      }
    },

    async sendToChannel(type, batch_id, cards, connection, typeOfPage = 'batch') {
      const connectionKey = connection.key

      try {
        const typeOfList = connectionKey === 'ebay' ? 'marketplace' : connectionKey
        // eBay's per-connection slot is keyed by the TEMPLATE type, not the
        // connection key — `ebay` is the CSV export profile, `ebay-direct` the
        // listing template this job uses.
        const perConnTemplate = this.selectedTemplatePerConnection[connectionKey === 'ebay' ? 'ebay-direct' : connectionKey]
        // A caller that set `selectedTemplate` itself wins — the Listings
        // details page builds a synthetic one from the listing's own policies.
        // Otherwise fall back to the connection's default, which is what every
        // entry point outside the publish menu has (the menu used to be the
        // only thing that filled this in, so listing from the grid cell sent no
        // template at all).
        const selectedTemplate = connectionKey === 'ebay'
          ? (this.selectedTemplate?.id ? this.selectedTemplate : (perConnTemplate ?? this.selectedTemplate))
          : (perConnTemplate?.id ? perConnTemplate : this[`${typeOfList}ListingTemplates`][0])
        this[`${connectionKey}SendError`] = false
        this[`${connectionKey}Sending`] = true

        let body_ = {
          template_id: selectedTemplate.id,
          job_method: 'create'
        }

        if (connectionKey === 'collx') {
          body_ = {
            listing_price: selectedTemplate.listing_price,
            price_adjustment_type: selectedTemplate.price_adjustment_type,
            start_price: selectedTemplate.start_price,
            calculated_method: selectedTemplate.calculated_method,
            fixed_amount_to_price: selectedTemplate.fixed_amount_to_price,
            percent_to_price: selectedTemplate.percent_to_price,
          }
        }

        const { data } = await apiClient().post(`/jobs/${type}`, {
          batch_id,
          type,
          data: body_,
          cards,
          export_type: connectionKey === 'ebay' ? 'ebay-direct' : connectionKey,
        })

        if (data?.ok) {
          // The other half of "has used the beta for real". Here
          // rather than at the four call sites (grid cell, listings cell,
          // inspector, publish menu) because this is the one choke point every
          // list-to-marketplace goes through. The action itself decides whether
          // this dealer is in the beta and whether it's a later session.
          useUserStore().markInventoryBetaSurveyEligible(INVENTORY_BETA_SURVEY_TRIGGER.CARD_LISTED)

          this[`${connectionKey}Sent`] = data.job?.status ? data.job?.status === 'pending' : true
          setTimeout(() => {
            if ((connectionKey === 'collx' && !this.collxAuthFailed) || ['ebay', 'whatnot', 'shopify'].includes(connectionKey)) {
              this.showExportToast = true
            }
          }, 1500)

          if (connectionKey === 'ebay' && typeOfPage === 'batch' && batch_id) {
            const batchStore = useBatchStore()

            batchStore.fetchBatch(batch_id)
          }
          
          this[`${connectionKey}Sending`] = false
          const info = { ...connection, countOfCards: cards.length }
          this.exportedChannelInfo = info
          const existingIdx = this.exportedChannelInfoList.findIndex(i => i.key === connectionKey)
          if (existingIdx >= 0) this.exportedChannelInfoList[existingIdx] = info
          else this.exportedChannelInfoList.push(info)
        } else {
          this[`${connectionKey}SendError`] = true
          this[`${connectionKey}Sending`] = false
        }
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'sendToChannel' })
        this[`${connectionKey}SendError`] = true
        this[`${connectionKey}Sending`] = false
      }
    },

    async fetchSentCollxJobs(options = {}) {
      try {
        const { page = 1, type = 'send-to-collx' } = options

        this.loadingExportsOrImports = true
        const { data } = await apiClient().get(`/jobs/${type}?page=${page}`)

        this.pagination = buildPagination({ data, totalItemsField: 'total_items' })

        this.exports = data.items || []
        this.loadingExportsOrImports = false
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'fetchSentCollxJobs' })
        this.loadingExportsOrImports = false
      }
    },

    async fetchSentCollxJobsById(jobId) {
      try {
        this.loadingExportsOrImports = true

        const { data } = await apiClient().get(`/jobs/id/${jobId}`)

        this.export = data || {}
        this.selectedTemplate = data || {}
        this.loadingExportsOrImports = false
      } catch (error) {
        this.loadingExportsOrImports = false
        handleApiError(error, { component: 'ConnectionStore', action: 'fetchSentCollxJobsById' })
      }
    },

    async fetchEbayPolicies() {
      const userStore = useUserStore()

      try {
        this.loadingPolicies = true

        const { data } = await apiClient().get(`/ebay/locations-and-policies`)

        if (data?.ok) {
          this.policies = data
        }

        this.loadingPolicies = false
      } catch (error) {
        this.loadingPolicies = false
        handleApiError(error, { component: 'ConnectionStore', action: 'fetchEbayPolicies' })
      }
    },

    async fetchEbayStoreCategories() {
      try {
        this.ebayStoreCategoriesNoSubscription = false

        const { data } = await apiClient().get(`/ebay/categories`)

        if (data) {
          this.ebayStoreCategories = data.storeCategories || []
        } else if (!data?.ok && data?.message === 'User does not have an active store subscription') {
          this.ebayStoreCategoriesNoSubscription = true
        } else {
          this.ebayStoreCategories = []
        }
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'fetchEbayStoreCategories' })
      }
    },

    async unlinkCollx(jobId) {
      const userStore = useUserStore()

      try {
        await apiClient().post(`/users/me/unlink-collx`)
        
        userStore.user.collx_user_id = null
        userStore.updateConnectionCredential('collx', { enabled: false })

        await userStore.fetchUser()
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'unlinkCollx' })
      }
    },

    async collxAuthFailedFunc() {
      const userStore = useUserStore()

      try {
        this.removeChannel(COLLX_CHANNEL)
        userStore.updateUser({ [COLLX_CHANNEL.field]: false })

        this.collxAuthFailed = true
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'collxAuthFailedFunc' })
      }
    },

    async createDefaultEbayPolicies() {
      const userStore = useUserStore()
      const userId = userStore.user?.id

      try {
        const policyTypes = ['fulfillment', 'payment', 'return']
        
        for (const policyType of policyTypes) {
          const storageKey = `ebay_default_${policyType}_policy_${userId}`
          const hasPolicy = localStorage.getItem(storageKey)

          if (!hasPolicy) {
            const { data } = await apiClient().post(`/ebay/create-default-${policyType}-policy`)
            
            if (data?.ok) {
              localStorage.setItem(storageKey, 'true')
            }
          }
        }
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'createDefaultEbayPolicies' })
      }
    },

    async optPolicies() {
      const userStore = useUserStore()
      const userId = userStore.user?.id

      try {
        const storageKey = `opt_policy_${userId}`
        const hasPolicy = localStorage.getItem(storageKey)

        if (!hasPolicy) {
          await apiClient().post(`/ebay/opt-in-to-policies`)
        }

        localStorage.setItem(storageKey, 'true')
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'optPolicies' })
      }
    },

    async linkToShopify(domain) {
      try {
        this.shopifyAuthLoader = true
        const { data } = await apiClient().post(`/users/me/link-shopify`, {
          shop_domain: domain
        })

        if (data.auth_url) {
          window.location.href = data.auth_url
        }
      }
      catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'linkToShopify' })
        this.shopifyAuthLoader = false
      }
    },

    async shopifyClaim(shop) {
      try {
        await apiClient().post(`/users/me/shopify/claim`, {
          shop
        })
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'shopifyClaim' })
      }
    },

    async fetchAvailableShopifyAttributes() {
      try {
        const { data } = await apiClient().get(`/shopify/mappings/attributes`)

        if (data?.attributes) {
          // Flatten nested attributes into array with labels
          const flattenedAttributes = []
          
          Object.entries(data.attributes).forEach(([category, attributes]) => {
            Object.entries(attributes).forEach(([key, value]) => {
              flattenedAttributes.push({
                key,
                category,
                label: value.label,
                type: value.type,
                ...value
              })
            })
          })
          
          this.metafieldsAvailable = flattenedAttributes
        }

        if (data?.tag_presets) {
          const flattenedTags = []
          const seenKeys = new Set()
          const excludedKeys = ['population', 'population_higher', 'population_lower']

          Object.entries(data.tag_presets).forEach(([category, attributes]) => {
            if (attributes.fields.length) {
              attributes.fields.forEach(field => {
                // Skip excluded keys and duplicates
                if (excludedKeys.includes(field) || seenKeys.has(field)) {
                  return
                }
                
                seenKeys.add(field)
                flattenedTags.push({
                  key: field,
                  category,
                  label: field,
                })
              })
            }
          })

          /**
           * They exist ONLY under `attributes.custom_fields` — no preset's
           * `fields` array lists them, and `custom.fields` ships empty — so a
           * picker built from `tag_presets` alone could never offer them, while
           * the metafield picker (built from `attributes`) always could. Same
           * flatten as metafields: the attribute KEY is the identifier
           * (`custom_fields.Card`), the label is what the user reads.
           *
           * `category: 'custom'` on purpose — that value is sent as
           * `preset_mode`, and the valid modes are the `tag_presets` keys;
           * `custom_fields` is not one of them.
           */
          Object.entries(data.attributes?.custom_fields || {}).forEach(([key, value]) => {
            if (seenKeys.has(key)) return

            seenKeys.add(key)
            flattenedTags.push({ key, category: 'custom', label: value?.label || key })
          })

          this.tagsAvailable = flattenedTags
        }
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'fetchAvailableShopifyAttributes' })
      }
    },

    async fetchMetafields() {
      try {
        const { data } = await apiClient().get(`/shopify/mappings/metafields`)

        this.metafields = data?.mappings || []
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'fetchMetafields' })
      }
    },

    async fetchTags() {
      try {
        const { data } = await apiClient().get(`/shopify/mappings/tags`)

        this.tags = data?.mappings || []
        this.presetMode = data?.preset_mode
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'fetchMetafields' })
      }
    },

    /**
     * The connected store's locations + the one CDP publishes into (CDP-2520).
     *
     * One request answers both questions the UI asks: whether there is anything
     * to choose between (`locations.length > 1`) and what is currently chosen.
     */
    async fetchShopifyLocations() {
      try {
        this.loadingShopifyLocations = true

        const { data } = await apiClient().get(`/users/me/shopify/locations`)

        this.shopifyLocations = data?.locations || []
        this.shopifyDefaultLocationId = data?.default_location_id ?? null
        this.shopifyLocationsLoaded = true
      } catch (error) {
        this.shopifyLocationsLoaded = false
        handleApiError(error, { component: 'ConnectionStore', action: 'fetchShopifyLocations' })
      } finally {
        this.loadingShopifyLocations = false
      }
    },

    /**
     * Save (or clear, with `null`) the default location.
     *
     * The id is stored optimistically so the row keeps showing the user's pick
     * while the request is in flight, and rolled back if it fails — this is a
     * setting people are warned not to change, so it must never look changed
     * when it wasn't.
     *
     * `/users/me` carries `default_location_id` too, so the user profile is
     * refreshed to keep the two copies from disagreeing.
     */
    async updateShopifyDefaultLocation(locationId) {
      const previous = this.shopifyDefaultLocationId

      try {
        this.shopifyDefaultLocationId = locationId ?? null

        await apiClient().put(`/users/me/shopify/default-location`, {
          location_id: locationId ?? null,
        })

        await useUserStore().fetchUser()

        return true
      } catch (error) {
        this.shopifyDefaultLocationId = previous
        handleApiError(error, { component: 'ConnectionStore', action: 'updateShopifyDefaultLocation' })

        return false
      }
    },

    async updateMetafield(newData, type = 'metafields', mode = 'custom') {
      try {
        this[type] = [ ...this[type], newData ]

        const body = {
          mappings: this[type]
        }

        if (type === 'tags') {
          body.preset_mode = mode
        }

        await apiClient().put(`/shopify/mappings/${type}`, body)
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'updateMetafield' })
      }
    },

    /**
     * Change one existing mapping in place — its field, or a tag's `format`.
     *
     * The endpoint only accepts the whole `mappings` array, so this is the same
     * PUT the add/delete actions make. `updateMetafield` can't be reused: it
     * appends, which is how re-picking a row's field used to ADD a mapping.
     * The row is matched by identity, not by `cdp_field` — static tags have
     * none, and two rows can map the same field.
     *
     * `preset_mode` is resent (unchanged unless the caller passes a new one),
     * and the list is rolled back on failure so the row never shows an unsaved
     * value.
     */
    async updateMapping(item, changes, type = 'tags', mode = this.presetMode) {
      const previous = this[type]
      const target = toRaw(item)

      try {
        this[type] = this[type].map(row => (toRaw(row) === target ? { ...row, ...changes } : row))

        const body = { mappings: this[type] }

        if (type === 'tags') {
          body.preset_mode = mode
        }

        await apiClient().put(`/shopify/mappings/${type}`, body)

        if (type === 'tags') this.presetMode = mode
      } catch (error) {
        this[type] = previous
        handleApiError(error, { component: 'ConnectionStore', action: 'updateMapping' })
      }
    },

    async deleteMetafield(deleteData, type = 'metafields') {
      try {
        let newData = [] 
        if (type === 'metafields') {
          newData = this[type].filter(item => item.shopify_key !== deleteData.shopify_key)
        } else {
          // By identity: matching on `cdp_field` removed EVERY static tag at
          // once, since none of them has one.
          const target = toRaw(deleteData)
          newData = this[type].filter(item => toRaw(item) !== target)
        }

        this[type] = newData || []

        await apiClient().put(`/shopify/mappings/${type}`, {
          mappings: newData || []
        })
      } catch (error) {
        handleApiError(error, { component: 'ConnectionStore', action: 'updateMetafield' })
      }
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useConnectionStore, import.meta.hot))
}
